/* eslint-disable no-unused-expressions */
import React, { useCallback, useEffect, useState } from "react";
import { RiEditLine } from "react-icons/ri";
import { IoClose } from "react-icons/io5";
import { debounce, fNO, searchCep } from "screens/perfilAutonomo/utils";
import { toast } from "react-toastify";
import { putEnderecoAutonomo } from "screens/perfilAutonomo/api/putEnderecoAutonomo";
import { Buffer } from "buffer";
import { FileUploadI, MAX_UPLOAD_SIZE } from "../FileUploadModal/utils";
import * as S from "./styles";
import * as Styles from "../modal.styles";
import { SearchBar } from "../SeachBar";
import { TextInput } from "../TextInput";
import { DragNDrop } from "../DragNDrop/dragNDrop.component";
import { SecondaryButton } from "../secondaryButton.component";
import { PrimaryButton } from "../primaryButton.component";
import { Loading } from "../Loading";

export const EnderecoModal = ({
  isOpen,
  onClose,
  user,
  authToken,
  refetch,
}: {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  authToken: string;
  refetch: () => void;
}) => {
  const [cepSearch, setCepSearch] = useState("");

  const [data, setData] = useState({
    logradouro: "",
    numero: "",
    bairro: "",
    cidade: "",
    estado: "",
    complemento: "",
  });
  const [comprovante, setComprovante] = useState<FileUploadI | null>(null);

  const [searching, setSearching] = useState(false);
  const [manualAddress, setManualAddress] = useState(false);

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCepSearch(user?.endereco?.cep ?? "");
      cep_(user?.endereco?.cep ?? "");
      setData({
        logradouro: user?.endereco?.logradouro ?? "",
        numero: user?.endereco?.numero ?? "",
        bairro: user?.endereco?.bairro ?? "",
        cidade: user?.endereco?.cidade ?? "",
        estado: user?.endereco?.estado ?? "",
        complemento: user?.endereco?.complemento ?? "",
      });
    }
  }, [isOpen]);

  const handleFileUpload = (file: File | null) => {
    if (!file) return;

    try {
      const reader = new FileReader();
      reader.onload = () => {
        const base64str = reader.result as string;

        if (!base64str || base64str === "") {
          toast.error(
            "Falha ao carregar o arquivo. Por favor, tente novamente."
          );
          return;
        }

        const decoded = Buffer.from(base64str, "base64").toString("binary");
        const fileSize = decoded.length;
        if (fileSize > MAX_UPLOAD_SIZE) {
          toast.error(
            `Tamanho do arquivo deve ser menor que ${
              MAX_UPLOAD_SIZE / 1024 / 1024
            }MB`
          );
        } else {
          setComprovante({
            nome: file.name,
            base64: base64str?.split(",").pop() as string,
            contentType: file.type,
          });
          toast.success("Arquivo carregado com sucesso!");
        }
      };
      reader.readAsDataURL(file);
    } catch (e) {
      console.error("Failed to decode base64 string:", e);
      toast.error("Falha ao carregar o arquivo. Por favor, tente novamente.");
    }
  };

  const cep_ = useCallback(
    debounce((cepSearch_) => {
      setSearching(true);

      searchCep(cepSearch_)
        .then((res) => {
          if (res.status === 200 && !res.data?.erro) {
            const resData = res?.data;
            setManualAddress(
              resData?.logradouro === "" || resData?.bairro === ""
            );

            setData((prev) => ({
              ...prev,
              logradouro: resData?.logradouro,
              bairro: resData?.bairro,
              cidade: resData?.localidade,
              estado: resData?.uf,
            }));
          } else throw new Error();
        })
        .catch(() => {
          toast.error(
            "Erro ao buscar CEP. Por favor, verifique os dados e tente novamente."
          );
        })
        .finally(() => {
          setSearching(false);
        });
    }, 1250),
    []
  );

  const handleSave = () => {
    setSaving(true);

    putEnderecoAutonomo({
      idAutonomo: user?.id,
      cep: cepSearch,
      logradouro: data?.logradouro,
      numero: data?.numero,
      complemento: data?.complemento,
      bairro: data?.bairro,
      cidade: data?.cidade,
      uf: data?.estado,
      arquivo: comprovante,
      authToken,
    })
      .then(({ success, notifications }) => {
        if (success) {
          toast.success(
            "Solicitação de alteração de endereço enviada com sucesso"
          );
          refetch();
          onClose();
        } else if (!success && notifications?.length > 0) {
          toast.info(notifications?.[0]?.message);
        } else throw new Error();
      })
      .catch((error) => {
        console.error(error);
        toast.error(
          "Erro ao solicitar alteração de endereço, por favor tente novamente"
        );
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const canSave =
    cepSearch.length === 8 &&
    data?.logradouro !== "" &&
    data?.numero !== "" &&
    data?.bairro !== "" &&
    data?.cidade !== "" &&
    data?.estado !== "" &&
    comprovante !== null;

  return (
    <Styles.DialogContainer open={isOpen} onClose={onClose} maxWidth="md">
      <Styles.Container style={{ minWidth: 500 }}>
        <Styles.IconContainer>
          <div className="icon-wrapper">
            <RiEditLine color="#4CAF50" />
          </div>
          <h1 className="title center">{"Editar Endereço"}</h1>
          <div style={{ padding: 8 }}>
            <IoClose size={26} className="exit" onClick={onClose} />
          </div>
        </Styles.IconContainer>

        <S.Container>
          <S.Row template="2.5fr 6fr 1.5fr">
            <S.Field>
              <p>
                {"CEP "}
                <span>*</span>
              </p>
              <SearchBar
                placeholder="00000-000"
                searchIcon="right"
                valueSearch={cepSearch}
                onChangeSearch={(val) => {
                  const v = fNO(val);
                  setCepSearch(v);
                  v.length === 8 && cep_(v);
                }}
                isLoading={searching}
                isError={cepSearch?.length < 8}
              />
            </S.Field>
            <S.Field>
              <p>
                {"Endereço "}
                {manualAddress && <span>*</span>}
              </p>
              <TextInput
                value={data?.logradouro}
                onChange={(val) => {
                  setData((prev) => ({ ...prev, logradouro: val }));
                }}
                disabled={!manualAddress}
              />
            </S.Field>
            <S.Field>
              <p>
                {"Número "}
                <span>*</span>
              </p>
              <TextInput
                value={data?.numero}
                onChange={(val) => {
                  setData((prev) => ({ ...prev, numero: val }));
                }}
                placeholder="123"
              />
            </S.Field>
          </S.Row>

          <S.Row template="1fr 1fr 1fr">
            <S.Field>
              <p>
                {"Bairro "}
                {manualAddress && <span>*</span>}
              </p>
              <TextInput
                value={data?.bairro}
                onChange={(val) => {
                  setData((prev) => ({ ...prev, bairro: val }));
                }}
                disabled={!manualAddress}
              />
            </S.Field>
            <S.Field>
              <p>Cidade</p>
              <TextInput
                value={data?.cidade}
                onChange={(val) => {
                  setData((prev) => ({ ...prev, cidade: val }));
                }}
                disabled
              />
            </S.Field>
            <S.Field>
              <p>Estado</p>
              <TextInput
                value={data?.estado}
                onChange={(val) => {
                  setData((prev) => ({ ...prev, estado: val }));
                }}
                disabled
              />
            </S.Field>
          </S.Row>

          <S.Field>
            <p>Complemento</p>
            <TextInput
              value={data?.complemento}
              onChange={(val) => {
                setData((prev) => ({ ...prev, complemento: val }));
              }}
            />
          </S.Field>

          <S.Field>
            <p>
              {"Incluir Comprovante de Endereço "}
              <span>*</span>
            </p>
            <DragNDrop
              onFilesSelected={(file) => {
                handleFileUpload(file);
              }}
              accept="image/png, image/jpeg, image/jpg, application/pdf"
              onChangeManual={(file) => {
                handleFileUpload(file);
              }}
              onRemoveFile={() => {
                setComprovante(null);
              }}
              removeIcon
            />
          </S.Field>
        </S.Container>

        <S.Row template="1fr 1fr">
          <div id="dummy" />
          <S.Row template="1fr 1fr">
            <SecondaryButton content="Cancelar" onClick={onClose} />
            <PrimaryButton
              content={saving ? <Loading size={18} color="white" /> : "Salvar"}
              onClick={handleSave}
              disabled={saving || !canSave}
            />
          </S.Row>
        </S.Row>
      </Styles.Container>
    </Styles.DialogContainer>
  );
};
